/**
 * Scaling Up KPIs — list + define + record manual value + force refresh.
 * Per-holding scope; can also filter by company. R/Y/G status badges.
 *
 * History charts and per-KPI detail page are scoped for follow-up iteration.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { AdminGuard } from "@/components/auth/AdminGuard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Plus, RefreshCw } from "lucide-react";

type KpiStatus = "red" | "yellow" | "green";
type KpiFreq = "daily" | "weekly" | "monthly" | "quarterly";
type KpiDataSource =
  | "manual"
  | "auto:internal"
  | "auto:stripe"
  | "auto:llm-cost"
  | "auto:meta-ads"
  | "auto:ga4"
  | "auto:powerbi";

interface Holding {
  id: string;
  name: string;
}
interface Company {
  id: string;
  name: string;
}

interface Kpi {
  id: string;
  name: string;
  description: string | null;
  frequency: KpiFreq;
  unit: string | null;
  target: string | null;
  redThreshold: string | null;
  yellowThreshold: string | null;
  higherIsBetter: boolean;
  currentValue: string | null;
  currentStatus: KpiStatus | null;
  lastUpdatedAt: string | null;
  dataSource: KpiDataSource;
  sourceConfig: Record<string, unknown>;
}

const STATUS_CLASS: Record<string, string> = {
  red: "bg-red-500/15 text-red-300 border-red-500/30",
  yellow: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

function ScalingUpKpisInner() {
  const qc = useQueryClient();
  const [holdingId, setHoldingId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string>("");
  const [showCreate, setShowCreate] = useState(false);

  const holdingsQuery = useQuery<{ items: Holding[] }>({
    queryKey: ["/api/scaling-up/holdings"],
  });
  const companiesQuery = useQuery<{ items: Company[] }>({
    queryKey: ["/api/scaling-up/holdings", holdingId ?? "", "companies"],
    enabled: holdingId !== null,
  });
  const kpisQueryKey: readonly string[] = [
    "/api/scaling-up/holdings",
    holdingId ?? "",
    `kpis${companyId ? `?companyId=${companyId}` : ""}`,
  ];
  const kpisQuery = useQuery<{ items: Kpi[] }>({
    queryKey: kpisQueryKey,
    enabled: holdingId !== null,
  });

  const refreshMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/scaling-up/kpis/${id}/refresh`),
    onSuccess: () => qc.invalidateQueries({ queryKey: kpisQueryKey }),
  });

  const recordMutation = useMutation({
    mutationFn: async (params: { id: string; value: number }) =>
      apiRequest("POST", `/api/scaling-up/kpis/${params.id}/values`, { value: params.value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kpisQueryKey }),
  });

  const holdings = holdingsQuery.data?.items ?? [];
  const companies = companiesQuery.data?.items ?? [];
  const kpis = kpisQuery.data?.items ?? [];

  return (
    <div className="min-h-screen bg-[#030407] text-white p-6 lg:p-10">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <div className="text-white/40 text-xs uppercase tracking-widest mb-1">Scaling Up</div>
          <h1 className="text-2xl lg:text-3xl font-extrabold">KPI Scoreboard</h1>
        </div>
        <Button size="sm" onClick={() => setShowCreate((v) => !v)} disabled={!holdingId}>
          <Plus className="w-4 h-4 mr-2" /> Nowy KPI
        </Button>
      </header>

      <Card className="mb-6 border-white/10 bg-white/[0.02]">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm"
            value={holdingId ?? ""}
            onChange={(e) => {
              setHoldingId(e.target.value || null);
              setCompanyId("");
            }}
          >
            <option value="">Wybierz holding…</option>
            {holdings.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm disabled:opacity-50"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            disabled={!holdingId}
          >
            <option value="">Wszystkie spółki</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {showCreate && holdingId && (
        <CreateKpiForm
          holdingId={holdingId}
          companyId={companyId || null}
          onCreated={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: kpisQueryKey });
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {kpisQuery.isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="border-white/10 bg-white/[0.02]">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <Skeleton className="h-4 w-2/3 mb-1.5" />
                    <Skeleton className="h-2.5 w-24" />
                  </div>
                  <Skeleton className="h-4 w-14 rounded-full" />
                </div>
                <Skeleton className="h-8 w-24 mb-2" />
                <Skeleton className="h-3 w-full" />
                <div className="mt-3">
                  <Skeleton className="h-8 w-28 rounded-md" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {kpis.length === 0 && !kpisQuery.isLoading && holdingId && (
        <div className="text-white/40 text-sm">Brak zdefiniowanych KPI.</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {kpis.map((k) => (
          <KpiCard
            key={k.id}
            kpi={k}
            onRefresh={() => refreshMutation.mutate(k.id)}
            onRecord={(value) => recordMutation.mutate({ id: k.id, value })}
            isRefreshing={refreshMutation.isPending}
          />
        ))}
      </div>
    </div>
  );
}

function KpiCard({
  kpi,
  onRefresh,
  onRecord,
  isRefreshing,
}: {
  kpi: Kpi;
  onRefresh: () => void;
  onRecord: (value: number) => void;
  isRefreshing: boolean;
}) {
  const [manualValue, setManualValue] = useState("");
  const isManual = kpi.dataSource === "manual";
  const statusBadge = kpi.currentStatus
    ? (STATUS_CLASS[kpi.currentStatus] ?? "bg-white/5 border-white/10")
    : "bg-white/5 border-white/10";

  return (
    <Card className="border-white/10 bg-white/[0.02]">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <div className="font-semibold">{kpi.name}</div>
            <div className="text-[10px] text-white/40 uppercase tracking-wider mt-0.5">
              {kpi.frequency} · {kpi.dataSource}
            </div>
          </div>
          {kpi.currentStatus && (
            <Badge className={`border text-[10px] ${statusBadge}`}>
              {kpi.currentStatus.toUpperCase()}
            </Badge>
          )}
        </div>
        <div className="text-2xl font-bold mb-1">
          {kpi.currentValue ?? "—"}
          {kpi.unit && <span className="text-sm text-white/40 ml-1">{kpi.unit}</span>}
        </div>
        <div className="text-xs text-white/50">
          target: {kpi.target ?? "—"} · yellow ≥ {kpi.yellowThreshold ?? "—"} · red ≥{" "}
          {kpi.redThreshold ?? "—"}
        </div>
        {kpi.lastUpdatedAt && (
          <div className="text-[10px] text-white/30 mt-1">
            ostatnio: {new Date(kpi.lastUpdatedAt).toLocaleString()}
          </div>
        )}

        <div className="mt-3 flex items-center gap-2">
          {isManual ? (
            <>
              <input
                type="number"
                step="any"
                value={manualValue}
                onChange={(e) => setManualValue(e.target.value)}
                className="flex-1 bg-black/30 border border-white/10 rounded px-2 py-1 text-sm"
                placeholder="wartość…"
              />
              <Button
                size="sm"
                variant="outline"
                disabled={!manualValue}
                onClick={() => {
                  const num = Number(manualValue);
                  if (Number.isFinite(num)) {
                    onRecord(num);
                    setManualValue("");
                  }
                }}
              >
                Zapisz
              </Button>
            </>
          ) : (
            <Button size="sm" variant="outline" onClick={onRefresh} disabled={isRefreshing}>
              {isRefreshing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Odśwież
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function CreateKpiForm({
  holdingId,
  companyId,
  onCreated,
  onCancel,
}: {
  holdingId: string;
  companyId: string | null;
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [frequency, setFrequency] = useState<KpiFreq>("weekly");
  const [unit, setUnit] = useState("");
  const [target, setTarget] = useState("");
  const [yellow, setYellow] = useState("");
  const [red, setRed] = useState("");
  const [higherIsBetter, setHigherIsBetter] = useState(true);
  const [dataSource, setDataSource] = useState<KpiDataSource>("manual");
  const [internalMetric, setInternalMetric] = useState<string>("users.total");

  const createMutation = useMutation({
    mutationFn: async () => {
      const sourceConfig = dataSource === "auto:internal" ? { metric: internalMetric } : {};
      return apiRequest("POST", `/api/scaling-up/holdings/${holdingId}/kpis`, {
        companyId,
        name,
        frequency,
        unit: unit || undefined,
        target: target ? Number(target) : undefined,
        yellowThreshold: yellow ? Number(yellow) : undefined,
        redThreshold: red ? Number(red) : undefined,
        higherIsBetter,
        dataSource,
        sourceConfig,
      });
    },
    onSuccess: onCreated,
  });

  return (
    <Card className="mb-4 border-white/10 bg-white/[0.04]">
      <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Nazwa">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Częstotliwość">
          <select
            className="input"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as KpiFreq)}
          >
            <option value="daily">daily</option>
            <option value="weekly">weekly</option>
            <option value="monthly">monthly</option>
            <option value="quarterly">quarterly</option>
          </select>
        </Field>
        <Field label="Jednostka">
          <input
            className="input"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="PLN, %, users…"
          />
        </Field>
        <Field label="Target">
          <input
            className="input"
            type="number"
            step="any"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
        </Field>
        <Field label="Yellow threshold">
          <input
            className="input"
            type="number"
            step="any"
            value={yellow}
            onChange={(e) => setYellow(e.target.value)}
          />
        </Field>
        <Field label="Red threshold">
          <input
            className="input"
            type="number"
            step="any"
            value={red}
            onChange={(e) => setRed(e.target.value)}
          />
        </Field>
        <Field label="Higher is better">
          <select
            className="input"
            value={higherIsBetter ? "true" : "false"}
            onChange={(e) => setHigherIsBetter(e.target.value === "true")}
          >
            <option value="true">Tak (np. revenue)</option>
            <option value="false">Nie (np. churn rate)</option>
          </select>
        </Field>
        <Field label="Data source">
          <select
            className="input"
            value={dataSource}
            onChange={(e) => setDataSource(e.target.value as KpiDataSource)}
          >
            <option value="manual">manual</option>
            <option value="auto:internal">auto:internal</option>
          </select>
        </Field>
        {dataSource === "auto:internal" && (
          <Field label="Internal metric">
            <select
              className="input"
              value={internalMetric}
              onChange={(e) => setInternalMetric(e.target.value)}
            >
              <option value="users.total">users.total</option>
              <option value="users.signups_30d">users.signups_30d</option>
              <option value="users.paid_active">users.paid_active</option>
              <option value="users.trial_active">users.trial_active</option>
            </select>
          </Field>
        )}
        <div className="md:col-span-2 flex items-center gap-2">
          <Button
            onClick={() => createMutation.mutate()}
            disabled={!name || createMutation.isPending}
          >
            {createMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Utwórz KPI
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Anuluj
          </Button>
          {createMutation.isError && (
            <span className="text-red-300 text-sm">
              {createMutation.error instanceof Error ? createMutation.error.message : "Błąd"}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs text-white/50 mb-1">{label}</div>
      {children}
    </label>
  );
}

export default function ScalingUpKpisPage() {
  return (
    <AdminGuard role="super_admin">
      <ScalingUpKpisInner />
    </AdminGuard>
  );
}
